import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Search, Loader2, Plus, X } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useOpportunityWizard } from './OpportunityWizardContext';
import OpportunityWizardLayout from './OpportunityWizardLayout';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Card, CardContent } from '@/components/ui/card';

interface Customer {
  id: string;
  name: string;
  legal_name: string;
  business_identification_number: string;
  domain?: string;
}

const Step1Customer = () => {
  const navigate = useNavigate();
  const { state, setSelectedCustomer } = useOpportunityWizard();
  const [searchQuery, setSearchQuery] = useState('');
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [filteredCustomers, setFilteredCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [newCustomer, setNewCustomer] = useState({
    name: '',
    legal_name: '',
    business_identification_number: '',
    email: '',
    phone: '',
    domain: ''
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchCustomers();
  }, []);

  useEffect(() => {
    if (searchQuery) {
      const filtered = customers.filter(
        customer =>
          customer.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          (customer.legal_name && customer.legal_name.toLowerCase().includes(searchQuery.toLowerCase())) ||
          (customer.business_identification_number && customer.business_identification_number.includes(searchQuery))
      );
      setFilteredCustomers(filtered);
    } else {
      setFilteredCustomers(customers);
    }
  }, [searchQuery, customers]);

  const fetchCustomers = async (query = '') => {
    try {
      setLoading(true);
      let queryBuilder = supabase
        .from('customers')
        .select('id, name, legal_name, business_identification_number')
        .order('name');

      if (query) {
        queryBuilder = queryBuilder.or(`name.ilike.%${query}%,legal_name.ilike.%${query}%,business_identification_number.ilike.%${query}%`);
      }

      const { data, error } = await queryBuilder.limit(10);

      if (error) throw error;
      setCustomers(data || []);
      setFilteredCustomers(data || []);
    } catch (error) {
      console.error('Error fetching customers:', error);
      toast.error('Falha ao carregar clientes');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    fetchCustomers(searchQuery);
  };

  const handleSelectCustomer = (customer: Customer) => {
    setSelectedCustomer(customer);
    navigate('../step2', { relative: 'path' });
  };

  // Função para validar CNPJ
  const validateCNPJ = (cnpj: string): boolean => {
    // Remover caracteres não numéricos
    cnpj = cnpj.replace(/[^\d]/g, '');
    
    // Verificar se tem 14 dígitos
    if (cnpj.length !== 14) return false;
    
    // Verificar se todos os dígitos são iguais
    if (/^(\d)\1+$/.test(cnpj)) return false;
    
    // Validação dos dígitos verificadores
    let tamanho = cnpj.length - 2;
    let numeros = cnpj.substring(0, tamanho);
    const digitos = cnpj.substring(tamanho);
    let soma = 0;
    let pos = tamanho - 7;
    
    // Primeiro dígito verificador
    for (let i = tamanho; i >= 1; i--) {
      soma += parseInt(numeros.charAt(tamanho - i)) * pos--;
      if (pos < 2) pos = 9;
    }
    
    let resultado = soma % 11 < 2 ? 0 : 11 - (soma % 11);
    if (resultado !== parseInt(digitos.charAt(0))) return false;
    
    // Segundo dígito verificador
    tamanho = tamanho + 1;
    numeros = cnpj.substring(0, tamanho);
    soma = 0;
    pos = tamanho - 7;
    
    for (let i = tamanho; i >= 1; i--) {
      soma += parseInt(numeros.charAt(tamanho - i)) * pos--;
      if (pos < 2) pos = 9;
    }
    
    resultado = soma % 11 < 2 ? 0 : 11 - (soma % 11);
    if (resultado !== parseInt(digitos.charAt(1))) return false;
    
    return true;
  };

  // Função para formatar CNPJ
  const formatCNPJ = (value: string): string => {
    // Remover caracteres não numéricos
    const cnpj = value.replace(/[^\d]/g, '');
    
    // Aplicar máscara
    if (cnpj.length <= 2) {
      return cnpj;
    } else if (cnpj.length <= 5) {
      return `${cnpj.slice(0, 2)}.${cnpj.slice(2)}`;
    } else if (cnpj.length <= 8) {
      return `${cnpj.slice(0, 2)}.${cnpj.slice(2, 5)}.${cnpj.slice(5)}`;
    } else if (cnpj.length <= 12) {
      return `${cnpj.slice(0, 2)}.${cnpj.slice(2, 5)}.${cnpj.slice(5, 8)}/${cnpj.slice(8)}`;
    } else {
      return `${cnpj.slice(0, 2)}.${cnpj.slice(2, 5)}.${cnpj.slice(5, 8)}/${cnpj.slice(8, 12)}-${cnpj.slice(12, 14)}`;
    }
  };

  // Handler para o campo de CNPJ
  const handleCNPJChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formattedValue = formatCNPJ(e.target.value);
    setNewCustomer({...newCustomer, business_identification_number: formattedValue});
    
    // Limpar erro quando o usuário começa a digitar
    if (formErrors.business_identification_number) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.business_identification_number;
        return newErrors;
      });
    }
  };

  const handleCreateCustomer = async () => {
    // Validar campos
    const errors: Record<string, string> = {};
    
    if (!newCustomer.name.trim()) {
      errors.name = 'Nome é obrigatório';
    }
    
    if (!newCustomer.legal_name.trim()) {
      errors.legal_name = 'Razão social é obrigatória';
    }
    
    // Validar CNPJ se foi preenchido
    if (newCustomer.business_identification_number) {
      const cnpj = newCustomer.business_identification_number.replace(/[^\d]/g, '');
      if (cnpj.length > 0 && !validateCNPJ(cnpj)) {
        errors.business_identification_number = 'CNPJ inválido';
      }
    }
    
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const { data, error } = await supabase
        .from('customers')
        .insert({
          name: newCustomer.name,
          legal_name: newCustomer.legal_name,
          business_identification_number: newCustomer.business_identification_number,
          email: newCustomer.email || null,
          phone: newCustomer.phone || null,
          domain: newCustomer.domain || null,
          status: 'active'
        })
        .select()
        .single();
      
      if (error) throw error;
      
      toast.success('Cliente criado com sucesso');
      
      // Atualizar lista de clientes
      fetchCustomers();
      
      // Selecionar o cliente recém-criado
      setSelectedCustomer(data);
      
      // Fechar o modal
      setIsCreateDialogOpen(false);
      
      // Navegar para o próximo passo
      navigate('../step2', { relative: 'path' });
    } catch (error) {
      console.error('Error creating customer:', error);
      toast.error('Erro ao criar cliente');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setNewCustomer({...newCustomer, [name]: value});
    
    // Limpar erro quando o usuário começa a digitar
    if (formErrors[name]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  return (
    <OpportunityWizardLayout
      currentStep={1}
      totalSteps={7}
      title="Selecionar Cliente"
      description="Selecione o cliente para criar uma nova oportunidade"
      onNext={() => {}}
      isNextDisabled={true}
    >
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row md:items-center gap-3">
          <div className="flex-1 flex gap-2">
            <div className="flex-1">
              <Input
                placeholder="Buscar cliente por nome, razão social ou CNPJ"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSearch()}
              />
            </div>
            <Button variant="outline" onClick={handleSearch}>
              <Search className="h-4 w-4" />
            </Button>
          </div>
          <Button className="md:w-auto" onClick={() => setIsCreateDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Novo Cliente
          </Button>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : filteredCustomers.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            Nenhum cliente encontrado
          </div>
        ) : (
          <div className="grid gap-3">
            {filteredCustomers.map(customer => (
              <Card 
                key={customer.id}
                className="cursor-pointer hover:bg-accent/50 transition-colors"
                onClick={() => handleSelectCustomer(customer)}
              >
                <CardContent className="p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-extrabold">{customer.name}</h3>
                      <div className="text-sm text-muted-foreground">
                        {customer.legal_name}
                      </div>
                      {customer.business_identification_number && (
                        <div className="text-sm text-muted-foreground mt-1">
                          CNPJ: {customer.business_identification_number}
                        </div>
                      )}
                    </div>
                    <Button 
                      size="sm" 
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSelectCustomer(customer);
                      }}
                    >
                      Selecionar
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Modal de criação de cliente */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Novo Cliente</DialogTitle>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nome *</Label>
                <Input 
                  id="name" 
                  name="name"
                  value={newCustomer.name}
                  onChange={handleInputChange}
                  placeholder="Nome do cliente"
                  className={formErrors.name ? "border-red-500" : ""}
                />
                {formErrors.name && <p className="text-red-500 text-xs">{formErrors.name}</p>}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="legal_name">Razão Social *</Label>
                <Input 
                  id="legal_name" 
                  name="legal_name"
                  value={newCustomer.legal_name}
                  onChange={handleInputChange}
                  placeholder="Razão social"
                  className={formErrors.legal_name ? "border-red-500" : ""}
                />
                {formErrors.legal_name && <p className="text-red-500 text-xs">{formErrors.legal_name}</p>}
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="business_identification_number">CNPJ</Label>
                <Input 
                  id="business_identification_number" 
                  name="business_identification_number"
                  value={newCustomer.business_identification_number}
                  onChange={handleCNPJChange}
                  placeholder="00.000.000/0000-00"
                  className={formErrors.business_identification_number ? "border-red-500" : ""}
                />
                {formErrors.business_identification_number && <p className="text-red-500 text-xs">{formErrors.business_identification_number}</p>}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="domain">Domínio</Label>
                <Input 
                  id="domain" 
                  name="domain"
                  value={newCustomer.domain}
                  onChange={handleInputChange}
                  placeholder="empresa.com.br"
                />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email">E-mail</Label>
                <Input 
                  id="email" 
                  name="email"
                  type="email"
                  value={newCustomer.email}
                  onChange={handleInputChange}
                  placeholder="contato@empresa.com.br"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="phone">Telefone</Label>
                <Input 
                  id="phone" 
                  name="phone"
                  value={newCustomer.phone}
                  onChange={handleInputChange}
                  placeholder="(00) 0000-0000"
                />
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setIsCreateDialogOpen(false)}
              disabled={isSubmitting}
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleCreateCustomer}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Salvando...
                </>
              ) : (
                'Salvar'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </OpportunityWizardLayout>
  );
};

export default Step1Customer; 